import React, { useState, useEffect } from 'react';
import { Form, Input, DatePicker, InputNumber, Button, Card, message } from 'antd';
import dayjs from 'dayjs';
import { createLetterProcessing, updateLetterProcessing } from '../../api/accounting';

const AspireForm = ({ selectedBrand, onSuccess, editRecord, setEditRecord }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Effect to populate form when editRecord changes
  useEffect(() => {
    if (editRecord) {
      // Format the date for the DatePicker component
      const formattedRecord = {
        ...editRecord,
        date: editRecord.date ? dayjs(editRecord.date) : null
      };
      
      form.setFieldsValue(formattedRecord);
    } else {
      form.resetFields();
    }
  }, [editRecord, form]);

  const onFinish = async (values) => {
    if (!selectedBrand) {
      message.error('Please select a brand first');
      return;
    }

    setLoading(true);
    try {
      // Add brand ID to the form data
      const formData = {
        ...values,
        brand: selectedBrand.id,
      };

      // Format date to YYYY-MM-DD
      if (formData.date) {
        formData.date = formData.date.format('YYYY-MM-DD');
      }

      // Convert any empty strings to null
      Object.keys(formData).forEach(key => {
        if (formData[key] === '') {
          formData[key] = null;
        }
      });

      if (editRecord) {
        // Update existing record
        await updateLetterProcessing(editRecord.id, formData);
        message.success('Letter processing record updated successfully');
        setEditRecord(null); // Clear edit mode
      } else {
        // Create new record
        await createLetterProcessing(formData);
        message.success('Letter processing record created successfully');
      }
      
      form.resetFields();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error creating letter processing record:', error.response || error);
      message.error('Failed to create letter processing record');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title={editRecord ? "Edit Letter Processing Record" : "Add Letter Processing Record"} className="mb-4">
      {editRecord && (
        <Button 
          type="link" 
          onClick={() => setEditRecord(null)} 
          style={{ marginBottom: '10px' }}
        >
          Cancel Edit
        </Button>
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          letters_received: 0,
          letters_processed: 0,
          check_orders: 0,
          check_orders_value: 0,
          money_orders: 0,
          money_orders_value: 0,
          cc_orders: 0,
          cc_orders_value: 0,
          cc_declined: 0,
          cash_orders: 0,
          cash_orders_value: 0,
          dnm: 0,
          misc: 0,
        }}
      >
        <div className="row">
          <div className="col-md-4">
            <Form.Item
              name="date"
              label="Date"
              rules={[{ required: true, message: 'Please select a date' }]}
            >
              <DatePicker className="w-100"  format="YYYY-MM-DD" />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item
              name="source"
              label="Source"
              rules={[{ required: true, message: 'Please enter the source' }]}
            >
              <Input />
            </Form.Item>
          </div>
          <div className="col-md-4">
            <Form.Item
              name="tracking_number"
              label="Tracking Number"
            >
              <Input />
            </Form.Item>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <Form.Item
              name="letters_received"
              label="Letters Received"
              rules={[{ required: true, message: 'Please enter the number of letters received' }]}
            >
              <InputNumber min={0} className="w-100" />
            </Form.Item>
          </div>
          <div className="col-md-6">
            <Form.Item
              name="letters_processed"
              label="Letters Processed"
              rules={[{ required: true, message: 'Please enter the number of letters processed' }]}
            >
              <InputNumber min={0} className="w-100" />
            </Form.Item>
          </div>
        </div>

        <h5 className="mt-3">Order Details</h5>
        <div className="row">
          <div className="col-md-3">
            <Form.Item
              name="check_orders"
              label="Check Orders"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={0} className="w-100" />
            </Form.Item>
          </div>
          <div className="col-md-3">
            <Form.Item
              name="check_orders_value"
              label="Value of Check Orders"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={0} step={0.01} precision={2} className="w-100" />
            </Form.Item>
          </div>
          <div className="col-md-3">
            <Form.Item
              name="money_orders"
              label="Money Orders"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={0} className="w-100" />
            </Form.Item>
          </div>
          <div className="col-md-3">
            <Form.Item
              name="money_orders_value"
              label="Value of Money Orders"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={0} step={0.01} precision={2} className="w-100" />
            </Form.Item>
          </div>
        </div>

        <div className="row">
          <div className="col-md-3">
            <Form.Item
              name="cc_orders"
              label="CC Orders"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={0} className="w-100" />
            </Form.Item>
          </div>
          <div className="col-md-3">
            <Form.Item
              name="cc_orders_value"
              label="Value of CC Orders"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={0} step={0.01} precision={2} className="w-100" />
            </Form.Item>
          </div>
          <div className="col-md-3">
            <Form.Item
              name="cc_declined"
              label="CC's Declined"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={0} className="w-100" />
            </Form.Item>
          </div>
          <div className="col-md-3">
            <Form.Item
              name="dnm"
              label="DNM (Do Not Mail)"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={0} className="w-100" />
            </Form.Item>
          </div>
        </div>

        <div className="row">
          <div className="col-md-3">
            <Form.Item
              name="cash_orders"
              label="Cash Orders"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={0} className="w-100" />
            </Form.Item>
          </div>
          <div className="col-md-3">
            <Form.Item
              name="cash_orders_value"
              label="Value of Cash Orders"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={0} step={0.01} precision={2} className="w-100" />
            </Form.Item>
          </div>
          <div className="col-md-6">
            <Form.Item
              name="misc"
              label="Miscellaneous"
              rules={[{ required: true, message: 'Required' }]}
            >
             <InputNumber min={0} step={0.01} precision={2} className="w-100" />
            </Form.Item>
          </div>
        </div>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Submit
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default AspireForm;
